/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { LoadRequest } from '@flight-details/load/models/load.request';
import { DecsService } from '@shared/services/decs/decs.service';
import { DateTime } from 'luxon';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Jumpseater } from './models/jumpseater';
import { StandbyListRequest } from './models/standby-list.request';
import { StandbyListService } from './standby-list.service';
import { StandbyListView } from './models/standby-list.view';
import { AlertService } from '@shared/services/alert/alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CrewHistoryService } from '@flight-details/crew/crew-history.service';

@Injectable({
  providedIn: 'root',
})
export class JumpseatService {
  isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private decsService: DecsService,
    private crewHistoryService: CrewHistoryService,
    private standbyListService: StandbyListService,
    private alertService: AlertService,
  ) {}

  getJumpseatList(
    request: StandbyListRequest,
    isRefresh: boolean,
  ): Observable<Jumpseater[]> {
    return this.getPNRs(request, isRefresh).pipe(
      switchMap((pnrs) => {
        const tobRequests: Observable<Jumpseater>[] = [];
        for (const pnr of pnrs) {
          if (pnr.trim().length === 6) {
            tobRequests.push(this.getTimeOfBooking(pnr, isRefresh));
          }
        }

        if (tobRequests.length === 0) {
          return of([]);
        }

        return forkJoin(tobRequests).pipe(
          map((jumpseaters) => this.sortByBookingSequence(jumpseaters)),
          map((jumpseaters) => this.populateFlownWith(jumpseaters)),
        );
      }),
      switchMap((jumpseaters) => {
        return this.standbyListService.getStandbyList(request, isRefresh).pipe(
          map((standbyListState) => {
            if (standbyListState.response) {
              const standbyListView = new StandbyListView(
                standbyListState.response.raw || '',
                'all',
                standbyListState.response.timestamp,
              );

              for (const jumpseater of jumpseaters) {
                for (const stby of standbyListView.list) {
                  if (
                    jumpseater.standbyListName ===
                      `${stby.firstName} ${stby.lastName}` &&
                    (stby.priority === 'D2W' || stby.priority === 'D1W')
                  ) {
                    jumpseater.isCheckedIn = true;
                    jumpseater.seat = stby.seat;
                  }
                }
              }
            }
            return jumpseaters;
          }),
        );
      }),
      tap(() => this.isLoading$.next(false)),
      catchError((err) => this.handleError(err)),
    );
  }

  private getPNRs(
    request: LoadRequest,
    isRefresh: boolean,
  ): Observable<string[]> {
    const cmd = `LD${request.flightNumber}/${DateTime.fromISO(request.date, {
      zone: 'utc',
    }).toFormat('ddMMM')}${request.depStation}-D`;
    return this.decsService.submitResRequest({ cmd }, isRefresh).pipe(
      tap((response) => {
        if (response.isLoading) {
          this.isLoading$.next(true);
        }
      }),
      filter((response) => response.response !== null),
      map((response) => {
        return this.parsePnrs(response.response?.result || '');
      }),
    );
  }

  private getTimeOfBooking(
    pnr: string,
    isRefresh: boolean,
  ): Observable<Jumpseater> {
    const cmd = `*${pnr}`;
    return this.decsService.submitResRequest({ cmd }, isRefresh).pipe(
      filter((response) => response.response !== null),
      map((response) =>
        this.parseTimeOfBooking(response.response?.result || ''),
      ),
    );
  }

  // Creates history$ observable and adds it to jumpseater.
  private populateFlownWith(jumpseaters: Jumpseater[]): Jumpseater[] {
    for (const jumpseater of jumpseaters) {
      // Create history observable for all pilots except self.
      jumpseater.history$ = this.crewHistoryService.getFlightsFlownWith(
        jumpseater.empNumber,
      );
    }

    return jumpseaters;
  }

  private parsePnrs(text: string): string[] {
    const pnrList: string[] = [];
    const lines = text.split('\n');

    for (const line of lines) {
      if (line.includes('D2W') || line.includes('D1W')) {
        pnrList.push(line.trim().slice(-6));
      }
    }

    return pnrList;
  }

  private parseTimeOfBooking(text: string): Jumpseater {
    const namePattern = /1\.1(?<lastName>.+)\/(?<firstName>.+)/;
    const empNumPattern = /SSR EMPL AA 00(?<empNum>\d{6})/;
    // const timeOfBookingPattern =
    //   /HDQ.HDQ5ETP (?<dateTime>\d{4}\/\d{2}[A-Z]{3}\d{2})/;
    // const timeOfBookingPattern =
    //   /[WWW.HDQ7SVD|HDQ.HDQ5ETP] (?<dateTime>\d{4}\/\d{2}[A-Z]{3}\d{2})/;
    const timeOfBookingPattern =
      /[WWW.HDQ\S+] (?<dateTime>\d{4}\/\d{2}[A-Z]{3}\d{2})/;
    const bookingSequencePattern =
      /BOOKING SEQUENCE - (?<bookingSequence>\d{1,})/;

    const nameMatch = text.match(namePattern);
    const empNumMatch = text.match(empNumPattern);
    const tobMatch = text.match(timeOfBookingPattern);
    const bookingSeqMatch = text.match(bookingSequencePattern);

    let name = '';
    let standbyListName = '';
    if (nameMatch && nameMatch.groups) {
      name = `${nameMatch.groups['firstName']} ${nameMatch.groups?.['lastName']}`;

      standbyListName = `${nameMatch.groups['firstName'].slice(
        0,
        5,
      )} ${nameMatch.groups['lastName'].slice(0, 10)}`;
    }

    let empNumber = '';
    if (empNumMatch && empNumMatch.groups) {
      empNumber = empNumMatch.groups['empNum'] || '';
    }

    let timeOfBooking = '';
    if (tobMatch && tobMatch.groups) {
      timeOfBooking = tobMatch.groups['dateTime'] || '';
    }

    let bookingSequence = -1;
    if (bookingSeqMatch && bookingSeqMatch.groups) {
      bookingSequence =
        parseInt(bookingSeqMatch.groups['bookingSequence']) || -1;
    }

    return {
      name,
      standbyListName,
      empNumber,
      listingDateTime: DateTime.fromFormat(timeOfBooking, 'HHmm/ddMMMyy', {
        zone: 'utc',
      }),
      bookingSequence,
    };
  }

  private sortByBookingSequence(jumpseaters: Jumpseater[]): Jumpseater[] {
    return jumpseaters.sort((a, b) => {
      return a.bookingSequence - b.bookingSequence;
    });
  }

  private handleError(err: HttpErrorResponse) {
    console.log('Jumpseat error.', err);
    this.isLoading$.next(false);
    this.alertService.setAlert({
      type: 'error',
      message: 'Failed to load jumpseat list.',
    });

    return [];
  }
}
